import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Car, Package, MapPin, Star, Navigation, Loader2, Clock, CheckCircle, Calendar, Tag, X, LogOut, User, CreditCard, Shield, Bell, MessageCircle, FileText, Square, CheckSquare, RefreshCw, Map, AlertCircle, Gift, CalendarIcon, Copy, Share2, Users, DollarSign } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import RideTracker from '../components/RideTracker';
import AddressAutocomplete from '../components/AddressAutocomplete';
import NotificationSettings from '../components/NotificationSettings';
import InstallPrompt from '../components/InstallPrompt';
import ProfilePictureUpload from '../components/ProfilePictureUpload';
import UserProfileModal from '../components/UserProfileModal';
import RideChat from '../components/RideChat';
import DriverLocationMap from '../components/DriverLocationMap';
import NearbyDriversMap from '../components/NearbyDriversMap';
import { playNotificationSound } from '../services/notificationSound';
import notificationManager from '../utils/notificationManager';

// Social sharing image URLs
const OG_IMAGE_RIDER = 'https://static.prod-images.emergentagent.com/jobs/e5897d24-f6fe-426c-9a64-f8bda4899382/images/fa71e98c66ceb1db85e40bc9679810a25e1c4cae9e1b806ba1d118e635cc1a73.png';

// Helper function to get current date in Eastern Time (YYYY-MM-DD format)
const getEasternDate = (daysToAdd = 0) => {
  const now = new Date();
  // Convert to Eastern Time
  const easternTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  // Add days if specified
  easternTime.setDate(easternTime.getDate() + daysToAdd);
  // Format as YYYY-MM-DD
  const year = easternTime.getFullYear();
  const month = String(easternTime.getMonth() + 1).padStart(2, '0');
  const day = String(easternTime.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const UserPortal = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'ride';
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Helper function to get full image URL
  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('data:')) {
      return imageUrl;
    }
    if (imageUrl.startsWith('/api/')) {
      return `${BACKEND_URL}${imageUrl}`;
    }
    return `${BACKEND_URL}/api${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  };
  
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authPhone, setAuthPhone] = useState('');
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('sedan');
  const [selectedTripType, setSelectedTripType] = useState('one-way');
  const [selectedHours, setSelectedHours] = useState(2);
  const [passengerName, setPassengerName] = useState('');
  const [passengerPhone, setPassengerPhone] = useState('');
  const [packageDescription, setPackageDescription] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Stops state
  const [stops, setStops] = useState([]);
  const [newStopAddress, setNewStopAddress] = useState('');
  const isManualPriceUpdate = useRef(false); // Flag to prevent debounce override
  
  // Scheduled ride state
  const [isScheduled, setIsScheduled] = useState(true); // Default ON
  const [scheduledDate, setScheduledDate] = useState(null); // Date object
  const [scheduledTime, setScheduledTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [slotError, setSlotError] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Referral discount state
  const [referralDiscount, setReferralDiscount] = useState({ discount: 0, rides_remaining: 0, message: null });
  
  // Round trip return time state
  const [returnDate, setReturnDate] = useState(null);
  const [returnTime, setReturnTime] = useState('');
  const [returnDatePickerOpen, setReturnDatePickerOpen] = useState(false);
  
  // Active ride tracking state
  const [activeRide, setActiveRide] = useState(null);
  const [rideDriver, setRideDriver] = useState(null);
  const [isLoadingRide, setIsLoadingRide] = useState(true);
  
  // WebSocket and real-time state
  const wsRef = useRef(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [newChatMessage, setNewChatMessage] = useState(null);
  const [driverArrived, setDriverArrived] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);

  // Ride history state
  const [rideHistory, setRideHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Nearby drivers map state - default to true so map is always visible
  const [showNearbyDriversMap, setShowNearbyDriversMap] = useState(true);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);

  // User profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);

  // User referral modal state
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [userReferralData, setUserReferralData] = useState(null);
  const [isLoadingReferral, setIsLoadingReferral] = useState(false);

  // Terms & Conditions state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsContent, setTermsContent] = useState({ title: 'Terms & Conditions', content: '' });
  const [isLoadingTerms, setIsLoadingTerms] = useState(false);

  // Check login status on mount and restore session
  useEffect(() => {
    const restoreSession = async () => {
      const userId = localStorage.getItem('user_id');
      const userName = localStorage.getItem('user_name');
      const userEmail = localStorage.getItem('user_email');
      
      if (userId) {
        setIsLoggedIn(true);
        
        // Try to fetch fresh user data from server
        try {
          const response = await axios.get(`${BACKEND_URL}/api/users/${userId}`);
          const userData = response.data.user || response.data;
          
          setCurrentUser({
            id: userData.id || userId,
            name: userData.name || userData.full_name || userName || 'User',
            email: userData.email || userEmail,
            profile_picture_url: userData.profile_picture_url
          });
          
          // Pre-fill name if available
          const displayName = userData.name || userData.full_name || userName;
          if (displayName) setPassengerName(displayName);
          
          // Update localStorage with fresh data
          if (userData.name || userData.full_name) {
            localStorage.setItem('user_name', userData.name || userData.full_name);
          }
          if (userData.email) {
            localStorage.setItem('user_email', userData.email);
          }
          
        } catch (error) {
          console.error('Error restoring user session:', error);
          // Use cached data if server fetch fails
          setCurrentUser({
            id: userId,
            name: userName || 'User',
            email: userEmail
          });
          if (userName) setPassengerName(userName);
        }
      }
    };
    
    restoreSession();
  }, [BACKEND_URL]);

  // Fetch ride history when user is logged in
  const fetchRideHistory = useCallback(async () => {
    if (!currentUser?.id) return;
    
    try {
      setIsLoadingHistory(true);
      const response = await axios.get(`${BACKEND_URL}/api/users/${currentUser.id}/rides/history?limit=10`);
      setRideHistory(response.data.rides || []);
    } catch (error) {
      console.error('Error fetching ride history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [currentUser?.id, BACKEND_URL]);

  // Fetch referral discount for current user
  const fetchReferralDiscount = useCallback(async () => {
    if (!currentUser?.id) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/referral/user/${currentUser.id}/discount`);
      setReferralDiscount(response.data);
    } catch (error) {
      console.error('Error fetching referral discount:', error);
      setReferralDiscount({ discount: 0, rides_remaining: 0, message: null });
    }
  }, [currentUser?.id, BACKEND_URL]);

  // Fetch user's referral code and stats
  const fetchUserReferralCode = useCallback(async () => {
    if (!currentUser?.id) return;
    
    setIsLoadingReferral(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/referral/my-code/${currentUser.id}`);
      setUserReferralData(response.data);
    } catch (error) {
      console.error('Error fetching referral code:', error);
      toast({
        title: "Error",
        description: "Could not load referral data",
        variant: "destructive"
      });
    } finally {
      setIsLoadingReferral(false);
    }
  }, [currentUser?.id, BACKEND_URL]);

  // Open referral modal and fetch data
  const openReferralModal = () => {
    setShowReferralModal(true);
    fetchUserReferralCode();
  };

  // Copy referral code to clipboard
  const copyReferralCode = async () => {
    if (!userReferralData?.referral_code) return;
    
    try {
      await navigator.clipboard.writeText(userReferralData.referral_code);
      toast({ title: "Copied!", description: "Referral code copied to clipboard" });
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = userReferralData.referral_code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast({ title: "Copied!", description: "Referral code copied to clipboard" });
    }
  };

  // Copy referral link to clipboard
  const copyReferralLink = async () => {
    if (!userReferralData?.referral_link) return;
    
    const link = `${window.location.origin}/user/signup?ref=${userReferralData.referral_code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast({ title: "Copied!", description: "Referral link copied to clipboard" });
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast({ title: "Copied!", description: "Referral link copied to clipboard" });
    }
  };

  // Share referral to social media
  const shareReferral = (platform) => {
    const link = `${window.location.origin}/user/signup?ref=${userReferralData?.referral_code}`;
    const message = `Save money on your first rides with J-Style Services! Use my referral code: ${userReferralData?.referral_code}`;
    const encodedUrl = encodeURIComponent(link);
    const encodedText = encodeURIComponent(message);
    
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    };
    
    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  // Fetch ride history and referral discount when user logs in
  useEffect(() => {
    if (isLoggedIn && currentUser?.id) {
      fetchRideHistory();
      fetchReferralDiscount();
    }
  }, [isLoggedIn, currentUser?.id, fetchRideHistory, fetchReferralDiscount]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!currentUser?.id || !activeRide) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/user/${currentUser.id}`;

    const connect = () => {
      try {
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          console.log('User WebSocket connected');
        };

        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('WebSocket message:', data);

            if (data.type === 'driver_location' && data.ride_id === activeRide?.id) {
              setDriverLocation(data.location);
            }
            
            if (data.type === 'driver_arrived' && data.ride_id === activeRide?.id) {
              setDriverArrived(true);
              // Play LOUD notification sound for driver arrival
              playNotificationSound('urgent');
              // Also vibrate longer for attention
              if (navigator.vibrate) {
                navigator.vibrate([500, 200, 500, 200, 500]);
              }
              
              toast({
                title: "🚗 Driver Has Arrived!",
                description: data.message || "Your driver is waiting outside.",
              });
            }
            
            if (data.type === 'chat_message' && data.message?.ride_id === activeRide?.id) {
              setNewChatMessage(data.message);
              // Show toast and play sound if chat is not open
              if (!showChat) {
                playNotificationSound('double');
                toast({
                  title: `New message from ${data.message.sender_name}`,
                  description: data.message.message?.slice(0, 50) + '...',
                });
              }
            }
            
            // Refresh ride history when ride status changes
            if (data.type === 'ride_status_update' || data.type === 'ride_completed') {
              fetchRideHistory();
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };

        wsRef.current.onclose = () => {
          console.log('User WebSocket disconnected');
          // Reconnect after 5 seconds
          setTimeout(connect, 5000);
        };

        wsRef.current.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
      } catch (error) {
        console.error('Error creating WebSocket:', error);
      }
    };

    connect();

    // Ping to keep connection alive
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [currentUser?.id, activeRide?.id, BACKEND_URL, showChat]);

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsAuthLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/users/login?email=${encodeURIComponent(authEmail)}&password=${encodeURIComponent(authPassword)}`);
      
      localStorage.setItem('user_id', response.data.user_id);
      localStorage.setItem('user_name', response.data.name || response.data.full_name);
      localStorage.setItem('user_email', response.data.email);
      
      setIsLoggedIn(true);
      setCurrentUser({
        id: response.data.user_id,
        name: response.data.name || response.data.full_name,
        email: response.data.email
      });
      setPassengerName(response.data.name || response.data.full_name || '');
      setShowAuth(false);
      
      // Subscribe to push notifications
      notificationManager.init().then(() => {
        notificationManager.subscribe(response.data.user_id, 'user');
      });
      
      toast({
        title: "Welcome Back!",
        description: "You're now logged in.",
      });
    } catch (error) {
      // For demo, allow any login
      if (authEmail && authPassword) {
        const name = authEmail.split('@')[0];
        const tempUserId = 'user_' + Date.now();
        localStorage.setItem('user_id', tempUserId);
        localStorage.setItem('user_name', name);
        localStorage.setItem('user_email', authEmail);
        
        setIsLoggedIn(true);
        setCurrentUser({ id: tempUserId, name, email: authEmail });
        setPassengerName(name);
        setShowAuth(false);
        
        // Subscribe to push notifications
        notificationManager.init().then(() => {
          notificationManager.subscribe(tempUserId, 'user');
        });
        
        toast({
          title: "Welcome!",
          description: "You're now logged in.",
        });
      } else {
        toast({
          title: "Login Failed",
          description: "Please check your credentials",
          variant: "destructive"
        });
      }
    } finally {
      setIsAuthLoading(false);
    }
  };

  // Handle signup
  const handleSignup = async (e) => {
    e.preventDefault();
    setIsAuthLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/users/signup`, {
        full_name: authName,
        email: authEmail,
        phone: authPhone,
        password: authPassword
      });
      
      localStorage.setItem('user_id', response.data.user_id);
      localStorage.setItem('user_name', authName);
      localStorage.setItem('user_email', authEmail);
      
      setIsLoggedIn(true);
      setCurrentUser({ id: response.data.user_id, name: authName, email: authEmail });
      setPassengerName(authName);
      setPassengerPhone(authPhone);
      setShowAuth(false);
      
      toast({
        title: "Account Created!",
        description: "Welcome to J-Style Services!",
      });
    } catch (error) {
      toast({
        title: "Signup Failed",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsAuthLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_email');
    setIsLoggedIn(false);
    setCurrentUser(null);
    toast({
      title: "Logged Out",
      description: "See you next time!",
    });
  };

  // Fetch active ride on load
  const fetchActiveRide = useCallback(async () => {
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      setIsLoadingRide(false);
      return;
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users/${userId}/rides/active`);
      setActiveRide(response.data.active_ride);
      setRideDriver(response.data.driver);
    } catch (error) {
      console.error('Error fetching active ride:', error);
    } finally {
      setIsLoadingRide(false);
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchActiveRide();
    
    // Poll for updates every 5 seconds if there's an active ride
    const interval = setInterval(() => {
      const userId = localStorage.getItem('user_id');
      if (userId) {
        fetchActiveRide();
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [fetchActiveRide]);

  // Vehicle options - starts empty, populated by API
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [estimatedDistance, setEstimatedDistance] = useState(null);
  const [isFetchingPrices, setIsFetchingPrices] = useState(false);

  const tripTypes = [
    { id: 'one-way', name: 'One Way', description: 'Single destination' },
    { id: 'round-trip', name: 'Round Trip', description: 'Return to pickup' },
    { id: 'hourly', name: 'Hourly', description: 'Multiple stops' }
  ];

  // Cache for geocoded addresses - initialize from localStorage for persistence
  const [geocodeCache, setGeocodeCache] = useState(() => {
    try {
      const cached = localStorage.getItem('jstyle_geocode_cache');
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });

  // Persist geocode cache to localStorage when it changes
  useEffect(() => {
    try {
      // Keep only last 100 entries to prevent localStorage bloat
      const entries = Object.entries(geocodeCache);
      if (entries.length > 100) {
        const trimmed = Object.fromEntries(entries.slice(-100));
        localStorage.setItem('jstyle_geocode_cache', JSON.stringify(trimmed));
      } else {
        localStorage.setItem('jstyle_geocode_cache', JSON.stringify(geocodeCache));
      }
    } catch (error) {
      console.warn('Failed to persist geocode cache:', error);
    }
  }, [geocodeCache]);

  // Simple hash function for consistent fallback coordinates
  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  };

  // Rate limiter for Nominatim (max 1 request per second)
  const lastGeocodingRequest = useRef(0);
  const geocodingQueue = useRef([]);

  // Sleep utility for rate limiting
  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Geocode address using Nominatim API with retry logic and rate limiting
  const geocodeAddress = async (address, retryCount = 0) => {
    const normalizedAddress = address.trim().toLowerCase();
    
    // Check in-memory cache first
    if (geocodeCache[normalizedAddress]) {
      return geocodeCache[normalizedAddress];
    }
    
    // Rate limiting: ensure at least 1 second between requests (Nominatim policy)
    const now = Date.now();
    const timeSinceLastRequest = now - lastGeocodingRequest.current;
    if (timeSinceLastRequest < 1000) {
      await sleep(1000 - timeSinceLastRequest);
    }
    lastGeocodingRequest.current = Date.now();
    
    // Clean and format the address for better geocoding
    let cleanedAddress = address.trim();
    // Add USA if not present and looks like a US address
    if (!cleanedAddress.toLowerCase().includes('usa') && 
        !cleanedAddress.toLowerCase().includes('united states') &&
        /\b(fl|florida|ga|georgia|ca|california|ny|new york|tx|texas|nc|nj|sc|va|md|pa|oh|il|mi|az|co|wa|or|nv|ma|ct|tn|al|la|ky|ok|mo|wi|mn|ia|ar|ks|ms|ut|ne|nm|wv|id|hi|nh|me|mt|ri|de|sd|nd|ak|dc|vt|wy)\b/i.test(cleanedAddress)) {
      cleanedAddress = cleanedAddress + ', USA';
    }
    
    // Helper function to search Nominatim
    const searchNominatim = async (query) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=us&limit=1&addressdetails=1`,
          {
            headers: {
              'Accept': 'application/json',
              'User-Agent': `JStyleServices/1.0 (${window.location.origin})`
            },
            signal: controller.signal
          }
        );
        
        clearTimeout(timeoutId);
        
        // Check if response is actually JSON (not an HTML error page)
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.warn('Nominatim returned non-JSON response, trying backup...');
          return null;
        }
        
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            return {
              lat: parseFloat(data[0].lat),
              lng: parseFloat(data[0].lon),
              displayName: data[0].display_name,
              timestamp: Date.now()
            };
          }
        }
        return null;
      } catch (err) {
        console.warn('Nominatim search error:', err.message);
        return null;
      }
    };
    
    // Backup geocoder using Photon (also free, based on OSM data)
    const searchPhoton = async (query) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        
        // Clean query for Photon - remove ", USA" and extra commas
        const cleanQuery = query.replace(/,\s*USA$/i, '').replace(/,\s*,/g, ',').trim();
        
        const response = await fetch(
          `https://photon.komoot.io/api/?q=${encodeURIComponent(cleanQuery)}&limit=1&lang=en`,
          {
            headers: { 'Accept': 'application/json' },
            signal: controller.signal
          }
        );
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          if (data && data.features && data.features.length > 0) {
            const feature = data.features[0];
            // Only use US results
            if (feature.properties?.country === 'United States') {
              return {
                lat: feature.geometry.coordinates[1],
                lng: feature.geometry.coordinates[0],
                displayName: feature.properties.name || query,
                timestamp: Date.now()
              };
            }
          }
        }
        return null;
      } catch (err) {
        console.warn('Photon search error:', err.message);
        return null;
      }
    };
    
    // Known Florida cities with coordinates (fallback for common cities)
    const floridaCities = {
      'hollywood': { lat: 26.0112, lng: -80.1495 },
      'miami': { lat: 25.7617, lng: -80.1918 },
      'fort lauderdale': { lat: 26.1224, lng: -80.1373 },
      'west palm beach': { lat: 26.7153, lng: -80.0534 },
      'pahokee': { lat: 26.8201, lng: -80.6653 },
      'belle glade': { lat: 26.6845, lng: -80.6676 },
      'delray beach': { lat: 26.4615, lng: -80.0728 },
      'boca raton': { lat: 26.3587, lng: -80.0831 },
      'key west': { lat: 24.5551, lng: -81.7800 },
      'orlando': { lat: 28.5383, lng: -81.3792 },
      'tampa': { lat: 27.9506, lng: -82.4572 },
      'jacksonville': { lat: 30.3322, lng: -81.6557 },
      'hialeah': { lat: 25.8576, lng: -80.2781 },
      'pembroke pines': { lat: 26.0128, lng: -80.3143 },
      'sunrise': { lat: 26.1339, lng: -80.1131 },
      'plantation': { lat: 26.1276, lng: -80.2331 },
      'coral springs': { lat: 26.2712, lng: -80.2706 },
      'pompano beach': { lat: 26.2379, lng: -80.1248 },
      'davie': { lat: 26.0765, lng: -80.2521 },
      'homestead': { lat: 25.4687, lng: -80.4776 }
    };
    
    // Local city lookup for Florida
    const lookupFloridaCity = (cityName) => {
      const normalized = cityName.toLowerCase().trim();
      if (floridaCities[normalized]) {
        return {
          ...floridaCities[normalized],
          displayName: `${cityName}, Florida`,
          timestamp: Date.now(),
          isLocalLookup: true
        };
      }
      return null;
    };
    
    try {
      // Try 1: Full address with Nominatim
      let coords = await searchNominatim(cleanedAddress);
      
      // Try 1b: If Nominatim failed, try Photon
      if (!coords) {
        console.log('[Geocoding] Nominatim failed, trying Photon...');
        coords = await searchPhoton(cleanedAddress);
      }
      
      // Try 2: If not found, try without street number
      if (!coords) {
        await sleep(1100); // Rate limiting
        const withoutNumber = cleanedAddress.replace(/^\d+\s+/, '');
        if (withoutNumber !== cleanedAddress) {
          coords = await searchNominatim(withoutNumber);
          if (!coords) {
            coords = await searchPhoton(withoutNumber);
          }
        }
      }
      
      // Try 3: If still not found, extract city/state/zip and search just that
      if (!coords) {
        await sleep(1100);
        // Extract zip code if present
        const zipMatch = cleanedAddress.match(/\b(\d{5})(?:-\d{4})?\b/);
        // Extract state abbreviation
        const stateMatch = cleanedAddress.match(/\b(fl|florida|ga|georgia|ca|california|ny|new york|tx|texas|nc|nj|sc|va|md|pa|oh|il|mi|az|co|wa|or|nv|ma|ct|tn|al|la|ky|ok|mo|wi|mn|ia|ar|ks|ms|ut|ne|nm|wv|id|hi|nh|me|mt|ri|de|sd|nd|ak|dc|vt|wy)\b/i);
        
        // Try to find city name - word immediately before state abbreviation
        const parts = cleanedAddress.split(/[\s,]+/).filter(p => p.length > 0);
        let cityName = '';
        if (stateMatch) {
          const stateIndex = parts.findIndex(p => p.toLowerCase() === stateMatch[1].toLowerCase());
          if (stateIndex > 0) {
            // Get the word immediately before state as city name
            cityName = parts[stateIndex - 1];
          }
        }
        
        console.log(`[Geocoding Fallback] City: ${cityName}, State: ${stateMatch?.[1]}, Zip: ${zipMatch?.[1]}`);
        
        // Try 3a: Use local Florida city lookup first (fastest, most reliable)
        if (cityName && stateMatch && stateMatch[1].toLowerCase() === 'fl') {
          coords = lookupFloridaCity(cityName);
          if (coords) {
            console.log(`[Geocoding] Found city in local lookup: ${cityName}`);
          }
        }
        
        // Try 3b: If not in local lookup, try API search
        if (!coords && cityName && stateMatch) {
          const cityStateQuery = zipMatch 
            ? `${cityName}, ${stateMatch[1]}, ${zipMatch[1]}, USA`
            : `${cityName}, ${stateMatch[1]}, USA`;
          console.log(`[Geocoding Fallback] Trying: ${cityStateQuery}`);
          coords = await searchNominatim(cityStateQuery);
          if (!coords) {
            coords = await searchPhoton(cityStateQuery);
          }
        }
        
        // Try 4: If still not found, try just zip code
        if (!coords && zipMatch) {
          await sleep(1100);
          console.log(`[Geocoding Fallback] Trying zip only: ${zipMatch[1]}`);
          coords = await searchNominatim(`${zipMatch[1]}, USA`);
          if (!coords) {
            coords = await searchPhoton(`${zipMatch[1]} USA`);
          }
        }
        
        // Try 5: If still not found, try just city and state with Photon
        if (!coords && cityName && stateMatch) {
          await sleep(500);
          console.log(`[Geocoding Fallback] Trying city+state only: ${cityName}, ${stateMatch[1]}`);
          coords = await searchPhoton(`${cityName} ${stateMatch[1]} USA`);
        }
      }
      
      if (coords) {
        // Cache the result
        setGeocodeCache(prev => ({ ...prev, [normalizedAddress]: coords }));
        return coords;
      }
      
      // If response not OK and we haven't retried too many times, retry with exponential backoff
      if (retryCount < 3) {
        const backoffTime = Math.pow(2, retryCount) * 1000;
        console.warn(`Nominatim search failed, retrying in ${backoffTime}ms...`);
        await sleep(backoffTime);
        return geocodeAddress(address, retryCount + 1);
      }
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('Geocoding request timed out for:', address);
      } else {
        console.error('Geocoding error:', error);
      }
      
      // Retry on network errors (up to 2 retries)
      if (retryCount < 2 && error.name !== 'AbortError') {
        await sleep(1000);
        return geocodeAddress(address, retryCount + 1);
      }
    }
    
    // Fallback to Miami coordinates with consistent offset based on address hash
    const hash = hashString(normalizedAddress);
    const fallbackCoords = {
      lat: 25.7617 + ((hash % 100) / 1000),  // Small consistent offset
      lng: -80.1918 + (((hash >> 8) % 100) / 1000),
      isFallback: true,
      timestamp: Date.now()
    };
    
    // Cache fallback but mark it as such
    setGeocodeCache(prev => ({ ...prev, [normalizedAddress]: fallbackCoords }));
    
    // Show warning toast if using fallback
    toast({
      title: "Location Note",
      description: "Using approximate location. For best results, include city/state in your address.",
      variant: "default"
    });
    
    return fallbackCoords;
  };

  // Fetch available time slots for scheduling
  const fetchAvailableSlots = async (date, vehicleType = null) => {
    if (!date) return;
    
    setIsLoadingSlots(true);
    setSlotError(null);
    
    try {
      const params = new URLSearchParams({ date });
      if (vehicleType) {
        params.append('vehicle_type', vehicleType);
      }
      params.append('service_type', activeTab === 'delivery' ? 'delivery' : 'ride');
      
      const response = await axios.get(`${BACKEND_URL}/api/schedule/available-slots?${params.toString()}`);
      // Store slots with both display time and datetime_utc
      const slots = response.data.slots || [];
      setAvailableSlots(slots);
      
      // Auto-select first available slot (use datetime_utc for actual value)
      const firstAvailable = slots.find(slot => slot.available);
      if (firstAvailable && !scheduledTime) {
        // Store the ISO datetime for the actual scheduling
        setScheduledTime(firstAvailable.datetime_utc);
      }
      
      // Show warning if no slots available
      if (!slots.some(slot => slot.available)) {
        setSlotError('No available time slots for this date. Please try another day.');
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      setSlotError('Could not load available times. Please try again.');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  // Check if selected time is available before booking
  const checkTimeAvailability = async (dateTime, vehicleType) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/schedule/check-availability?scheduled_datetime=${dateTime}&vehicle_type=${vehicleType || ''}`
      );
      return response.data;
    } catch (error) {
      console.error('Error checking availability:', error);
      return { available: false, reason: 'Could not verify availability' };
    }
  };

  // Fetch live prices when pickup and dropoff change
  const fetchPriceEstimates = useCallback(async (serviceType = null, overrideStopsCount = null) => {
    if (!pickup || !dropoff) return;
    
    // Determine service type based on active tab if not provided
    const effectiveServiceType = serviceType || (activeTab === 'delivery' ? 'delivery' : 'transportation');
    
    // IMPORTANT: Always use overrideStopsCount when provided (fixes async state issue)
    const stopsCount = overrideStopsCount !== null ? overrideStopsCount : stops.length;
    
    setIsFetchingPrices(true);
    try {
      const coords = await geocodeAddress(pickup);
      const dropCoords = await geocodeAddress(dropoff);
      
      // Store coords for nearby drivers map and auto-refresh
      setPickupCoords(coords);
      setDropoffCoords(dropCoords);
      
      const response = await axios.post(`${BACKEND_URL}/api/rides/estimate`, {
        pickup_lat: coords.lat,
        pickup_lng: coords.lng,
        dropoff_lat: dropCoords.lat,
        dropoff_lng: dropCoords.lng,
        trip_type: selectedTripType,
        stops: stopsCount,
        hours: selectedTripType === 'hourly' ? selectedHours : null,
        service_type: effectiveServiceType
      });
      
      if (response.data.estimates && response.data.estimates.length > 0) {
        const newOptions = response.data.estimates.map(est => ({
          id: est.vehicle_id,
          name: est.vehicle_name,
          price: est.estimated_price,
          time: `${est.estimated_time} min`,
          icon: Car,
          seats: est.max_passengers,
          description: est.description || `${est.vehicle_name} vehicle`,
          loading: false,
          service_types: est.service_types || ['transportation'],
          per_stop: est.per_stop || 2.00,  // Store per-stop price from backend
          hourly_rate: est.hourly_rate || 0  // Store hourly rate from backend
        }));
        setVehicleOptions(newOptions);
        setEstimatedDistance(response.data.distance_miles);
        
        // Auto-select first vehicle if none selected or current selection not in list
        if (!selectedVehicle || !newOptions.find(v => v.id === selectedVehicle)) {
          setSelectedVehicle(newOptions[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching price estimates:', error);
      // Keep showing loading state on error
    } finally {
      setIsFetchingPrices(false);
    }
    // Note: stops is intentionally NOT in deps - we use overrideStopsCount param instead
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup, dropoff, selectedTripType, selectedHours, BACKEND_URL, selectedVehicle, activeTab]);

  // Debounce price fetching - only trigger on address/trip type changes
  useEffect(() => {
    if (!pickup || !dropoff) {
      // Reset to loading state when addresses are cleared
      setVehicleOptions(prev => prev.map(v => ({ ...v, price: null, time: '-- min', loading: true })));
      setEstimatedDistance(null);
      return;
    }
    
    // Skip if we just did a manual price update (e.g., from stop add/remove)
    if (isManualPriceUpdate.current) {
      console.log('[Debounce] Skipping - manual price update in progress');
      isManualPriceUpdate.current = false;
      return;
    }
    
    const serviceType = activeTab === 'delivery' ? 'delivery' : 'transportation';
    
    const debounceTimer = setTimeout(() => {
      console.log('[Debounce] Fetching prices with stops:', stops.length);
      fetchPriceEstimates(serviceType, stops.length);
    }, 800); // Wait 800ms after user stops typing
    
    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup, dropoff, selectedTripType, selectedHours, activeTab]);

  // Auto-refresh prices every 30 seconds to catch admin price changes
  useEffect(() => {
    // Only run if we have valid addresses, coords, and prices loaded
    if (!pickup || !dropoff || !pickupCoords || !dropoffCoords || vehicleOptions.length === 0) return;
    
    const refreshInterval = setInterval(() => {
      // Silent refresh - don't show loading state to avoid UI flicker
      const serviceType = activeTab === 'delivery' ? 'delivery' : 'transportation';
      
      // Fetch updated prices in background using stored coordinates
      axios.post(`${BACKEND_URL}/api/rides/estimate`, {
        pickup_lat: pickupCoords.lat,
        pickup_lng: pickupCoords.lng,
        dropoff_lat: dropoffCoords.lat,
        dropoff_lng: dropoffCoords.lng,
        trip_type: selectedTripType,
        stops: stops.length,
        hours: selectedTripType === 'hourly' ? selectedHours : null,
        service_type: serviceType
      }).then(response => {
        if (response.data.estimates && response.data.estimates.length > 0) {
          // Update prices silently without changing selection
          setVehicleOptions(prev => {
            return prev.map(vehicle => {
              const updated = response.data.estimates.find(e => e.vehicle_id === vehicle.id);
              if (updated && updated.estimated_price !== vehicle.price) {
                console.log(`[Auto-refresh] Price updated for ${vehicle.name}: $${vehicle.price} → $${updated.estimated_price}`);
                return { ...vehicle, price: updated.estimated_price, per_stop: updated.per_stop || vehicle.per_stop };
              }
              return vehicle;
            });
          });
        }
      }).catch(err => {
        // Silent fail - don't disrupt user experience
        console.log('[Auto-refresh] Price refresh failed:', err.message);
      });
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(refreshInterval);
  }, [pickup, dropoff, pickupCoords, dropoffCoords, vehicleOptions.length, activeTab, selectedTripType, stops.length, selectedHours, BACKEND_URL]);

  // Validate and apply promo code
  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      toast({
        title: "Enter Promo Code",
        description: "Please enter a promo code to apply",
        variant: "destructive"
      });
      return;
    }
    
    const selectedOption = vehicleOptions.find(v => v.id === selectedVehicle);
    const rideAmount = selectedOption?.price || 0;
    
    setIsValidatingPromo(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/promos/validate?code=${promoCode}&ride_amount=${rideAmount}`);
      
      setAppliedPromo({
        code: response.data.code,
        discount: response.data.calculated_discount,
        finalAmount: response.data.final_amount,
        description: response.data.description
      });
      
      toast({
        title: "Promo Applied! 🎉",
        description: `You saved $${response.data.calculated_discount.toFixed(2)}`,
      });
    } catch (error) {
      toast({
        title: "Invalid Promo Code",
        description: error.response?.data?.detail || "This promo code is not valid",
        variant: "destructive"
      });
      setAppliedPromo(null);
    } finally {
      setIsValidatingPromo(false);
    }
  };

  // Remove applied promo code
  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode('');
  };

  // Update prices after stop count changes
  const updatePricesForStops = async (newStopsCount, serviceType = 'transportation') => {
    console.log('[updatePricesForStops] Called with stops:', newStopsCount, 'service:', serviceType);
    console.log('[updatePricesForStops] pickup:', pickup, 'dropoff:', dropoff);
    
    if (!pickup || !dropoff) {
      console.log('[updatePricesForStops] No pickup/dropoff, returning');
      return;
    }
    
    // Set flag to prevent debounce from overriding our update
    isManualPriceUpdate.current = true;
    
    try {
      setIsFetchingPrices(true);
      
      // Use cached coordinates or geocode
      let pickupCoords, dropoffCoords;
      try {
        pickupCoords = await geocodeAddress(pickup);
        dropoffCoords = await geocodeAddress(dropoff);
      } catch (geoError) {
        console.error('[updatePricesForStops] Geocoding failed, using fallback:', geoError);
        // Use fallback Miami coordinates
        pickupCoords = { lat: 25.7617, lng: -80.1918 };
        dropoffCoords = { lat: 25.7717, lng: -80.2018 };
      }
      
      console.log('[updatePricesForStops] Making API call with stops:', newStopsCount);
      const response = await axios.post(`${BACKEND_URL}/api/rides/estimate`, {
        pickup_lat: pickupCoords.lat,
        pickup_lng: pickupCoords.lng,
        dropoff_lat: dropoffCoords.lat,
        dropoff_lng: dropoffCoords.lng,
        trip_type: selectedTripType,
        stops: newStopsCount,
        hours: selectedTripType === 'hourly' ? selectedHours : null,
        service_type: serviceType
      });
      
      console.log('[updatePricesForStops] API Response estimates:', response.data.estimates?.length);
      
      if (response.data.estimates?.length > 0) {
        const newOptions = response.data.estimates.map(est => ({
          id: est.vehicle_id,
          name: est.vehicle_name,
          price: est.estimated_price,
          time: `${est.estimated_time} min`,
          icon: Car,
          seats: est.max_passengers,
          description: est.description || `${est.vehicle_name} vehicle`,
          loading: false,
          service_types: est.service_types || ['transportation'],
          per_stop: est.per_stop || 2.00,
          hourly_rate: est.hourly_rate || 0
        }));
        console.log('[updatePricesForStops] Updating prices:', newOptions.map(v => `${v.name}: $${v.price}`));
        setVehicleOptions(newOptions);
        setEstimatedDistance(response.data.distance_miles);
        toast({ title: "Price Updated", description: `Now showing price for ${newStopsCount} stop(s)` });
      }
    } catch (error) {
      console.error('[updatePricesForStops] Error:', error);
      toast({ title: "Error", description: "Failed to update price", variant: "destructive" });
    } finally {
      setIsFetchingPrices(false);
    }
  };

  // Fetch Terms & Conditions
  const fetchTermsConditions = async () => {
    try {
      setIsLoadingTerms(true);
      const response = await axios.get(`${BACKEND_URL}/api/terms`);
      setTermsContent({
        title: response.data.title || 'Terms & Conditions',
        content: response.data.content || ''
      });
    } catch (error) {
      console.error('Error fetching terms:', error);
    } finally {
      setIsLoadingTerms(false);
    }
  };

  // Open Terms modal
  const openTermsModal = () => {
    fetchTermsConditions();
    setShowTermsModal(true);
  };

  // Get final price (with promo discount and referral discount if applicable)
  const getFinalPrice = () => {
    const selectedOption = vehicleOptions.find(v => v.id === selectedVehicle);
    let basePrice = selectedOption?.price || 0;
    
    // Apply promo discount first
    if (appliedPromo) {
      basePrice = Math.max(0, basePrice - appliedPromo.discount);
    }
    
    // Apply referral discount if user has remaining rides
    if (referralDiscount.discount > 0 && referralDiscount.rides_remaining > 0) {
      basePrice = Math.max(0, basePrice - referralDiscount.discount);
    }
    
    return basePrice;
  };

  // Get referral discount amount for display
  const getReferralDiscountAmount = () => {
    if (referralDiscount.discount > 0 && referralDiscount.rides_remaining > 0) {
      return referralDiscount.discount;
    }
    return 0;
  };

  // Get per-stop price for selected vehicle (dynamically from API)
  const getPerStopPrice = () => {
    const selectedOption = vehicleOptions.find(v => v.id === selectedVehicle);
    return selectedOption?.per_stop || 2.00;  // Default to $2 if not found
  };

  const handleBooking = async () => {
    // Require user to be logged in
    if (!isLoggedIn) {
      toast({
        title: "Sign In Required",
        description: "Please sign in or create an account to book a ride",
        variant: "destructive"
      });
      setAuthMode('signup');
      setShowAuth(true);
      return;
    }

    if (!pickup || !dropoff) {
      toast({
        title: "Missing Information",
        description: "Please enter pickup and drop-off locations",
        variant: "destructive"
      });
      return;
    }

    if (!passengerName || !passengerPhone) {
      toast({
        title: "Contact Required",
        description: "Please enter your name and phone number",
        variant: "destructive"
      });
      return;
    }

    // Validate Terms & Conditions acceptance
    if (!termsAccepted) {
      toast({
        title: "Terms & Conditions Required",
        description: "Please accept the Terms & Conditions to continue",
        variant: "destructive"
      });
      return;
    }

    // Validate round trip return time
    if (selectedTripType === 'round-trip') {
      if (!returnDate || !returnTime) {
        toast({
          title: "Return Time Required",
          description: "Please select when you need to come back for your round trip",
          variant: "destructive"
        });
        return;
      }
      
      // Create returnDateTime from Date object and time string
      const returnDateTime = new Date(returnDate);
      const [hours, minutes] = returnTime.split(':').map(Number);
      returnDateTime.setHours(hours, minutes, 0, 0);
      const now = new Date();
      
      if (returnDateTime <= now) {
        toast({
          title: "Invalid Return Time",
          description: "Return time must be in the future",
          variant: "destructive"
        });
        return;
      }
    }

    // Validate scheduled ride
    if (isScheduled) {
      if (!scheduledDate || !scheduledTime) {
        toast({
          title: "Schedule Required",
          description: "Please select both date and time for your scheduled ride",
          variant: "destructive"
        });
        return;
      }
      
      // scheduledTime is now in ISO format (datetime_utc from slot)
      const scheduledDateTime = new Date(scheduledTime);
      const now = new Date();
      const maxDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      // Only check if time is in the past (lead time handled by slot availability)
      if (scheduledDateTime <= now) {
        toast({
          title: "Invalid Time",
          description: "Please select a future time for your scheduled ride",
          variant: "destructive"
        });
        return;
      }
      
      if (scheduledDateTime > maxDate) {
        toast({
          title: "Too Far Ahead",
          description: "You can only schedule rides up to 30 days in advance",
          variant: "destructive"
        });
        return;
      }
      
      // Check time slot availability before proceeding
      const availability = await checkTimeAvailability(scheduledDateTime.toISOString(), selectedVehicle);
      if (!availability.available) {
        toast({
          title: "Time Slot Unavailable",
          description: availability.reason || "This time slot is already booked. Please select another time.",
          variant: "destructive"
        });
        // Refresh available slots
        fetchAvailableSlots(scheduledDate, selectedVehicle);
        return;
      }
    }

    setIsProcessing(true);

    try {
      // Geocode addresses
      const pickupCoords = await geocodeAddress(pickup);
      const dropoffCoords = await geocodeAddress(dropoff);

      // Build scheduled_time if scheduling
      // scheduledTime is now in "YYYY-MM-DD HH:MM:SS" Eastern Time format
      let scheduled_time = null;
      if (isScheduled && scheduledTime) {
        // Send the time string directly - backend will handle it as Eastern Time
        scheduled_time = scheduledTime;
      }

      // Prepare booking request
      const bookingData = {
        pickup: {
          address: pickup,
          lat: pickupCoords.lat,
          lng: pickupCoords.lng
        },
        dropoff: {
          address: dropoff,
          lat: dropoffCoords.lat,
          lng: dropoffCoords.lng
        },
        vehicle_type: selectedVehicle,
        trip_type: selectedTripType,
        stops: stops.map(stop => ({
          address: stop.address,
          lat: stop.lat || 0,
          lng: stop.lng || 0
        })),
        passenger_name: passengerName,
        passenger_phone: passengerPhone,
        special_instructions: activeTab === 'delivery' ? packageDescription : specialInstructions,
        scheduled_time: scheduled_time,
        user_id: currentUser?.id || localStorage.getItem('user_id')
      };

      console.log('Booking data:', bookingData);

      // Call backend API
      const response = await axios.post(`${BACKEND_URL}/api/rides/book`, bookingData);
      
      console.log('Booking response:', response.data);

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        toast({
          title: "Redirecting to Payment",
          description: `Total: $${response.data.estimated_price.toFixed(2)}`,
        });
        
        // Redirect to Stripe
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error) {
      console.error('Booking error:', error);
      setIsProcessing(false);
      
      toast({
        title: "Booking Failed",
        description: error.response?.data?.detail || error.message || "Please try again",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
      {/* SEO & Social Sharing Meta Tags */}
      <Helmet>
        <title>J-Style Services - Book Your Ride</title>
        <meta name="description" content="Book premium rides and deliveries with J-Style Services. Safe, reliable, and affordable transportation." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="J-Style Services - Book Your Ride" />
        <meta property="og:description" content="Book premium rides and deliveries. Safe, reliable, and affordable transportation at your fingertips." />
        <meta property="og:image" content={OG_IMAGE_RIDER} />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="J-Style Services - Book Your Ride" />
        <meta name="twitter:description" content="Book premium rides and deliveries with J-Style Services." />
        <meta name="twitter:image" content={OG_IMAGE_RIDER} />
        <link rel="manifest" href="/manifest-user.json" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="J-Style Rider" />
      </Helmet>

      {/* Notification Settings Modal */}
      <NotificationSettings 
        isOpen={showNotificationSettings} 
        onClose={() => setShowNotificationSettings(false)}
        userType="user"
      />

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={currentUser}
        backendUrl={BACKEND_URL}
        onProfileUpdate={(updatedUser) => {
          setCurrentUser(updatedUser);
        }}
      />

      {/* User Referral Modal */}
      {showReferralModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <Card className="w-full max-w-lg p-6 relative animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setShowReferralModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
            
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <Gift className="text-white" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Refer & Earn</h2>
              <p className="text-gray-500 text-sm mt-1">
                Share your code with friends and both of you save!
              </p>
            </div>

            {isLoadingReferral ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-purple-500" size={32} />
              </div>
            ) : userReferralData ? (
              <div className="space-y-6">
                {/* Referral Code Card */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border-2 border-purple-200">
                  <p className="text-sm text-purple-600 font-medium mb-2">Your Referral Code</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white px-4 py-3 rounded-lg border-2 border-purple-300 font-mono text-2xl font-bold text-purple-800 text-center tracking-wider">
                      {userReferralData.referral_code}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyReferralCode}
                      className="border-purple-300 hover:bg-purple-100"
                      data-testid="copy-referral-code"
                    >
                      <Copy size={18} className="text-purple-600" />
                    </Button>
                  </div>
                </div>

                {/* Referral Link */}
                <div>
                  <p className="text-sm text-gray-600 font-medium mb-2">Shareable Link</p>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={`${window.location.origin}/user/signup?ref=${userReferralData.referral_code}`}
                      readOnly
                      className="text-sm bg-gray-50"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyReferralLink}
                      className="border-gray-300 hover:bg-gray-100"
                    >
                      <Copy size={18} />
                    </Button>
                  </div>
                </div>

                {/* Share Buttons */}
                <div>
                  <p className="text-sm text-gray-600 font-medium mb-2">Share via</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                      onClick={() => shareReferral('facebook')}
                    >
                      <span className="font-bold mr-1">f</span> Facebook
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 border-gray-800 text-gray-800 hover:bg-gray-100"
                      onClick={() => shareReferral('twitter')}
                    >
                      <span className="font-bold mr-1">𝕏</span> Twitter
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 border-green-500 text-green-600 hover:bg-green-50"
                      onClick={() => shareReferral('whatsapp')}
                    >
                      WhatsApp
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200 text-center">
                    <Users className="mx-auto mb-2 text-blue-600" size={24} />
                    <p className="text-2xl font-bold text-blue-800">{userReferralData.total_referrals || 0}</p>
                    <p className="text-xs text-blue-600">Friends Referred</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200 text-center">
                    <DollarSign className="mx-auto mb-2 text-green-600" size={24} />
                    <p className="text-2xl font-bold text-green-800">${(userReferralData.total_earned || 0).toFixed(2)}</p>
                    <p className="text-xs text-green-600">Total Earned</p>
                  </div>
                </div>

                {/* How it Works */}
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Share2 size={16} /> How It Works
                  </p>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p className="flex items-start gap-2">
                      <span className="w-5 h-5 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">1</span>
                      Share your referral code with friends
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="w-5 h-5 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">2</span>
                      They sign up using your code
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="w-5 h-5 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">3</span>
                      They get discounts on their first rides
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="w-5 h-5 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">4</span>
                      You earn credits when they complete rides!
                    </p>
                  </div>
                </div>

                {/* Available Credit */}
                {userReferralData.available_credit > 0 && (
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-xl border-2 border-amber-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-amber-700">Available Credit</p>
                        <p className="text-2xl font-bold text-amber-800">${userReferralData.available_credit.toFixed(2)}</p>
                      </div>
                      <Gift className="text-amber-500" size={32} />
                    </div>
                    <p className="text-xs text-amber-600 mt-2">Credit will be applied to your next ride!</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Could not load referral data. Please try again.</p>
                <Button onClick={fetchUserReferralCode} className="mt-4" variant="outline">
                  <RefreshCw size={16} className="mr-2" /> Retry
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-6 relative animate-in fade-in zoom-in-95">
            <button 
              onClick={() => setShowAuth(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
            
            <div className="text-center mb-6">
              <img src={logoUrl} alt="Logo" className="h-16 w-16 mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-gray-900">
                {authMode === 'login' ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                {authMode === 'login' ? 'Sign in to your account' : 'Join J-Style Services'}
              </p>
            </div>

            <form onSubmit={authMode === 'login' ? handleLogin : handleSignup} className="space-y-4">
              {authMode === 'signup' && (
                <>
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      value={authName}
                      onChange={(e) => setAuthName(e.target.value)}
                      placeholder="John Doe"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Phone Number</Label>
                    <Input
                      value={authPhone}
                      onChange={(e) => setAuthPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                      required
                      className="mt-1"
                    />
                  </div>
                </>
              )}
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-1"
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={isAuthLoading}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600"
              >
                {isAuthLoading ? (
                  <Loader2 className="animate-spin mr-2" size={18} />
                ) : null}
                {authMode === 'login' ? 'Sign In' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-4 text-center text-sm">
              {authMode === 'login' ? (
                <p className="text-gray-600">
                  Don't have an account?{' '}
                  <button onClick={() => setAuthMode('signup')} className="text-amber-600 font-semibold hover:underline">
                    Sign up
                  </button>
                </p>
              ) : (
                <p className="text-gray-600">
                  Already have an account?{' '}
                  <button onClick={() => setAuthMode('login')} className="text-amber-600 font-semibold hover:underline">
                    Sign in
                  </button>
                </p>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-2 xs:px-3 sm:px-4 py-2 sm:py-4 flex justify-between items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src={logoUrl} alt="Logo" className="h-8 w-8 sm:h-10 sm:w-10 object-contain flex-shrink-0" />
            <span className="text-base sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent whitespace-nowrap">J-Style Services</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 md:gap-3 flex-shrink-0">
            {/* Notification Settings Button */}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowNotificationSettings(true)}
              className="text-slate-300 hover:text-amber-400 hover:bg-slate-800 p-1.5 sm:p-2"
              title="Notification Settings"
            >
              <Bell size={16} className="sm:w-[18px] sm:h-[18px]" />
            </Button>
            
            {isLoggedIn ? (
              <>
                {/* Refer & Earn Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="border-purple-500 text-purple-400 hover:bg-purple-500/20 hidden sm:flex"
                  onClick={openReferralModal}
                  data-testid="refer-earn-btn"
                >
                  <Gift size={16} className="mr-1" />
                  Refer & Earn
                </Button>
                {/* Profile Button */}
                <button
                  onClick={() => setShowProfileModal(true)}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-600 transition-colors"
                  title="My Profile"
                  data-testid="user-profile-btn"
                >
                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full overflow-hidden bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                    {currentUser?.profile_photo || localStorage.getItem('user_photo') ? (
                      <img 
                        src={getFullImageUrl(currentUser?.profile_photo || localStorage.getItem('user_photo'))} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="text-white" size={18} />
                    )}
                  </div>
                  <span className="hidden md:inline text-sm text-slate-300">{currentUser?.name}</span>
                </button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleLogout}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
                >
                  <LogOut size={16} className="mr-1" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
                  onClick={() => { setAuthMode('login'); setShowAuth(true); }}
                >
                  Sign In
                </Button>
                <Button 
                  size="sm" 
                  className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 font-semibold hidden sm:flex"
                  onClick={() => { setAuthMode('signup'); setShowAuth(true); }}
                >
                  Sign Up
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-2 xs:px-3 sm:px-4 py-3 sm:py-4 md:py-8 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-3 sm:gap-4 md:gap-8">
          {/* Booking Form */}
          <div>
            <Card className="p-3 xs:p-4 sm:p-6 border-2 border-slate-700 bg-white shadow-xl">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-4 sm:mb-6">
                  <TabsTrigger value="ride" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-yellow-600 data-[state=active]:text-white">
                    <Car size={16} className="sm:w-[18px] sm:h-[18px]" />
                    Ride
                  </TabsTrigger>
                  <TabsTrigger value="delivery" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-yellow-600 data-[state=active]:text-white">
                    <Package size={16} className="sm:w-[18px] sm:h-[18px]" />
                    Delivery
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="ride" className="space-y-3 sm:space-y-4">
                  <div className="space-y-3 sm:space-y-4">
                    {/* Contact Info */}
                    <div className="grid grid-cols-2 gap-3">
                      <Input 
                        placeholder="Your name"
                        value={passengerName}
                        onChange={(e) => setPassengerName(e.target.value)}
                        className="border-2 focus:border-amber-500"
                      />
                      <Input 
                        placeholder="Phone number"
                        value={passengerPhone}
                        onChange={(e) => setPassengerPhone(e.target.value)}
                        className="border-2 focus:border-amber-500"
                      />
                    </div>

                    {/* Locations with Autocomplete */}
                    {/* 1. PICKUP */}
                    <div className="relative z-30">
                      <MapPin className="absolute left-3 top-3 text-green-600 z-10" size={20} />
                      <AddressAutocomplete
                        placeholder="Enter pickup location"
                        className="pl-10 py-6 border-2 focus:border-amber-500"
                        value={pickup}
                        onChange={setPickup}
                        onSelect={(loc) => {
                          setPickup(loc.address);
                        }}
                        data-testid="input-pickup"
                      />
                    </div>

                    {/* 2. ADD STOPS - Always visible */}
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-lg p-3 space-y-2" data-testid="stops-section">
                      <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                        <MapPin size={12} />
                        ADD STOPS (optional, +${getPerStopPrice().toFixed(2)} each) {stops.length > 0 && `- ${stops.length} added`}
                      </p>
                      {/* Existing Stops - scrollable when many */}
                      {stops.length > 0 && (
                        <div className={`space-y-2 ${stops.length > 5 ? 'max-h-48 overflow-y-auto pr-1' : ''}`}>
                          {stops.map((stop, index) => (
                            <div key={index} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-amber-300 shadow-sm">
                              <div className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                                {index + 1}
                              </div>
                              <span className="flex-1 text-sm truncate">{stop.address}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                                onClick={async () => {
                                  console.log('[Remove Stop] Clicked, current stops:', stops.length);
                                  const newStops = stops.filter((_, i) => i !== index);
                                  console.log('[Remove Stop] New stops count:', newStops.length);
                                  setStops(newStops);
                                  setNewStopAddress('');
                                  toast({ title: "Stop Removed", description: `Updating price (${newStops.length} stops)...` });
                                  await updatePricesForStops(newStops.length, 'transportation');
                                  console.log('[Remove Stop] Price update complete');
                                }}
                                data-testid={`remove-stop-${index}`}
                              >
                                <X size={14} />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add New Stop Input */}
                      {stops.length < 100 && (
                        <div className="relative z-20">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-600 z-10" />
                          <AddressAutocomplete
                            placeholder={`+ Add stop ${stops.length + 1}`}
                            className="pl-10 py-3 border-2 border-dashed border-amber-400 focus:border-amber-500 text-sm bg-white"
                            value={newStopAddress}
                            onChange={setNewStopAddress}
                            onSelect={(loc) => {
                              if (loc.address) {
                                isManualPriceUpdate.current = true; // Prevent debounce override
                                const newStopsCount = stops.length + 1;
                                setStops([...stops, { address: loc.address, lat: loc.lat || 0, lng: loc.lng || 0 }]);
                                setNewStopAddress('');
                                toast({ title: "Stop Added", description: `+$${getPerStopPrice().toFixed(2)} added to fare` });
                                updatePricesForStops(newStopsCount, 'transportation');
                              }
                            }}
                            data-testid="input-add-stop"
                          />
                        </div>
                      )}
                      
                      {stops.length > 0 && (
                        <div className="flex items-center justify-between bg-amber-100 py-1 px-2 rounded">
                          <p className="text-xs text-amber-700 font-semibold">
                            +${(stops.length * getPerStopPrice()).toFixed(2)} for {stops.length} stop{stops.length > 1 ? 's' : ''}
                          </p>
                          {stops.length > 3 && (
                            <button 
                              type="button"
                              onClick={async () => {
                                setStops([]);
                                toast({ title: "All Stops Cleared" });
                                await updatePricesForStops(0, 'transportation');
                              }}
                              className="text-xs text-red-600 hover:text-red-800 underline"
                            >
                              Clear All
                            </button>
                          )}
                        </div>
                      )}
                      
                      {stops.length >= 100 && (
                        <p className="text-xs text-gray-500 italic text-center">Maximum 100 stops reached</p>
                      )}
                    </div>

                    {/* 3. DROPOFF (Final Destination) */}
                    <div className="relative">
                      <Navigation className="absolute left-3 top-3 text-red-600 z-10" size={20} />
                      <AddressAutocomplete
                        placeholder="Final destination"
                        className="pl-10 py-6 border-2 focus:border-amber-500"
                        value={dropoff}
                        onChange={setDropoff}
                        onSelect={(loc) => {
                          setDropoff(loc.address);
                        }}
                        data-testid="input-dropoff"
                      />
                    </div>
                  </div>

                  {/* Trip Type Selection */}
                  <div className="space-y-2 pt-2">
                    <h3 className="font-semibold text-sm">Trip Type</h3>
                    <div className="grid grid-cols-3 gap-1 sm:gap-2">
                      {tripTypes.map((type) => (
                        <Card 
                          key={type.id}
                          className={`p-2 sm:p-3 cursor-pointer transition-all text-center ${
                            selectedTripType === type.id ? 'border-2 border-amber-500 bg-amber-50' : 'border hover:border-amber-300'
                          }`}
                          onClick={() => setSelectedTripType(type.id)}
                          data-testid={`trip-type-${type.id}`}
                        >
                          <p className="font-semibold text-xs sm:text-sm">{type.name}</p>
                          <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">{type.description}</p>
                        </Card>
                      ))}
                    </div>
                  </div>

                  {/* Hours selector for hourly trips */}
                  {selectedTripType === 'hourly' && (
                    <div className="space-y-2 pt-2">
                      <h3 className="font-semibold text-sm">How many hours?</h3>
                      <div className="flex flex-wrap gap-2">
                        {[1, 2, 3, 4, 6, 8].map((hours) => (
                          <Button
                            key={hours}
                            size="sm"
                            variant={selectedHours === hours ? 'default' : 'outline'}
                            className={`${selectedHours === hours ? 'bg-amber-500 hover:bg-amber-600' : ''} min-w-[40px]`}
                            onClick={() => setSelectedHours(hours)}
                            data-testid={`hours-${hours}`}
                          >
                            {hours}h
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Return Time for Round Trip */}
                  {selectedTripType === 'round-trip' && (
                    <div className="space-y-3 pt-2">
                      <div className="p-3 sm:p-4 bg-blue-50 rounded-lg border-2 border-blue-200 space-y-3">
                        <div className="flex items-center gap-2">
                          <Clock className="text-blue-500 flex-shrink-0" size={18} />
                          <h3 className="font-semibold text-xs sm:text-sm text-blue-800">When do you need to come back?</h3>
                        </div>
                        <p className="text-xs text-blue-700">Set the date and time for your return trip</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-blue-700 font-medium">Return Date</label>
                            <Popover open={returnDatePickerOpen} onOpenChange={setReturnDatePickerOpen}>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className={`mt-1 w-full justify-start text-left font-normal border-2 border-blue-300 hover:bg-blue-50 ${!returnDate && 'text-muted-foreground'}`}
                                  data-testid="return-date"
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4 text-blue-600" />
                                  {returnDate ? format(returnDate, 'EEE, MMM d, yyyy') : <span>Select return date</span>}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                  mode="single"
                                  selected={returnDate}
                                  onSelect={(date) => {
                                    setReturnDate(date);
                                    setReturnDatePickerOpen(false);
                                  }}
                                  disabled={(date) => date < new Date() || date > addDays(new Date(), 30)}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <label className="text-xs text-blue-700 font-medium">Return Time</label>
                            <Input
                              type="time"
                              value={returnTime}
                              onChange={(e) => setReturnTime(e.target.value)}
                              className="border-2 border-blue-300 focus:border-blue-500 mt-1"
                              data-testid="return-time"
                            />
                          </div>
                        </div>
                        {returnDate && returnTime && (
                          <p className="text-xs sm:text-sm text-blue-800 font-medium">
                            ✓ Return scheduled for {format(returnDate, 'EEE, MMM d')} at {returnTime}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Schedule Ride Option - Always Visible */}
                  <div className="space-y-3 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border-2 border-amber-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                          <Calendar className="text-white" size={16} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-sm text-slate-800">Schedule for Later</h3>
                          <p className="text-xs text-gray-500">Book up to 30 days in advance</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => setIsScheduled(!isScheduled)}
                        className={`relative w-14 h-7 rounded-full transition-colors shadow-inner ${isScheduled ? 'bg-amber-500' : 'bg-gray-300'}`}
                        data-testid="schedule-toggle"
                      >
                        <span className={`absolute w-6 h-6 bg-white rounded-full top-0.5 transition-transform shadow-md ${isScheduled ? 'translate-x-7' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    
                    {isScheduled && (
                      <div className="p-3 sm:p-4 bg-amber-50 rounded-lg border-2 border-amber-200 space-y-3">
                        <p className="text-xs sm:text-sm text-amber-800">
                          <Clock size={14} className="inline mr-1" />
                          Schedule your {activeTab === 'delivery' ? 'delivery' : 'ride'} up to 30 days in advance.
                        </p>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-amber-800 text-sm">Date *</Label>
                            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className={`mt-1 w-full justify-start text-left font-normal border-amber-300 hover:bg-amber-50 ${!scheduledDate && 'text-muted-foreground'}`}
                                  data-testid="schedule-date"
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4 text-amber-600" />
                                  {scheduledDate ? format(scheduledDate, 'EEE, MMM d, yyyy') : <span>Select date</span>}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                  mode="single"
                                  selected={scheduledDate}
                                  onSelect={(date) => {
                                    setScheduledDate(date);
                                    setScheduledTime('');
                                    setDatePickerOpen(false);
                                    if (date) {
                                      const dateStr = format(date, 'yyyy-MM-dd');
                                      fetchAvailableSlots(dateStr, selectedVehicle);
                                    }
                                  }}
                                  disabled={(date) => date < new Date() || date > addDays(new Date(), 30)}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <Label className="text-amber-800 text-xs sm:text-sm">Time * {isLoadingSlots && <Loader2 className="inline animate-spin ml-1" size={12} />}</Label>
                            {scheduledDate && availableSlots.length > 0 ? (
                              <div className="mt-1 grid grid-cols-4 sm:grid-cols-5 gap-1.5 max-h-48 overflow-y-auto p-2 bg-gradient-to-b from-amber-50/50 to-white rounded-lg border border-amber-200 shadow-inner">
                                {availableSlots.map((slot) => (
                                  <button
                                    key={slot.datetime_utc}
                                    type="button"
                                    disabled={!slot.available}
                                    onClick={() => setScheduledTime(slot.datetime_utc)}
                                    className={`px-1.5 py-2 text-xs font-medium rounded-md transition-all duration-150 ${
                                      scheduledTime === slot.datetime_utc 
                                        ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-md scale-105 ring-2 ring-amber-300' 
                                        : slot.available 
                                          ? 'bg-white text-gray-700 hover:bg-amber-100 hover:text-amber-800 border border-gray-200 hover:border-amber-300 hover:shadow-sm' 
                                          : 'bg-gray-100 text-gray-400 cursor-not-allowed line-through opacity-60'
                                    }`}
                                    title={slot.reason || (slot.available ? 'Available' : 'Booked')}
                                    data-testid={`time-slot-${slot.time?.replace(/[:\s]/g, '')}`}
                                  >
                                    {slot.time}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="mt-1 p-3 bg-gray-50 rounded border border-amber-300 text-center">
                                <p className="text-xs text-gray-500">{scheduledDate ? 'Loading times...' : 'Select a date first'}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {slotError && (
                          <p className="text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle size={12} /> {slotError}
                          </p>
                        )}
                        
                        {scheduledTime && !slotError && (
                          <p className="text-xs text-green-700 flex items-center gap-1">
                            <CheckCircle size={12} /> 
                            Scheduled for {new Date(scheduledTime).toLocaleString([], { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric', 
                              hour: 'numeric', 
                              minute: '2-digit' 
                            })}
                          </p>
                        )}
                        
                        <p className="text-xs text-amber-700">
                          ⏰ A driver will be notified 30 minutes before pickup.
                          <br />
                          <span className="text-amber-600">We operate 24/7! Red slots are already booked.</span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Vehicle Selection */}
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">Choose a vehicle</h3>
                      <div className="flex items-center gap-2">
                        {estimatedDistance && (
                          <span className="text-sm text-gray-500">{estimatedDistance.toFixed(1)} miles</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Nearby Drivers Map - Always visible when pickup location is set */}
                    {pickupCoords && (
                      <NearbyDriversMap
                        pickupLat={pickupCoords.lat}
                        pickupLng={pickupCoords.lng}
                        pickupAddress={pickup}
                        backendUrl={BACKEND_URL}
                        onClose={null}
                        selectedVehicleType={selectedVehicle ? vehicleOptions.find(v => v.id === selectedVehicle)?.name : null}
                      />
                    )}
                    
                    {isFetchingPrices && (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin text-amber-500" size={24} />
                        <span className="ml-2 text-gray-500">Loading vehicle options...</span>
                      </div>
                    )}
                    {!isFetchingPrices && vehicleOptions.length === 0 && (
                      <Card className="p-6 text-center text-gray-500 border-2 border-dashed" data-testid="vehicles-empty-state">
                        <Car size={40} className="mx-auto mb-3 text-amber-400" />
                        <p className="font-medium text-gray-700">No vehicles loaded yet</p>
                        <p className="text-sm mt-1">Enter pickup and drop-off locations above to see available vehicles and prices</p>
                      </Card>
                    )}
                    {!isFetchingPrices && vehicleOptions.length > 0 && vehicleOptions.map((vehicle) => {
                      const Icon = vehicle.icon;
                      return (
                        <Card 
                          key={vehicle.id}
                          className={`p-3 sm:p-4 cursor-pointer transition-all hover:shadow-md ${
                            selectedVehicle === vehicle.id ? 'border-2 border-amber-500 bg-amber-50' : 'border-2'
                          }`}
                          onClick={() => setSelectedVehicle(vehicle.id)}
                          data-testid={`vehicle-${vehicle.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Icon className="text-slate-700" size={20} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="font-semibold text-sm sm:text-base truncate">{vehicle.name}</h4>
                                <p className="text-xs text-gray-500 truncate">{vehicle.description}</p>
                                <p className="text-xs text-gray-500">{vehicle.time} away • {vehicle.seats} seats</p>
                                {/* Per-stop price - updates automatically from admin settings */}
                                {vehicle.per_stop && (
                                  <p className="text-xs text-amber-600 font-medium mt-0.5" data-testid={`vehicle-${vehicle.id}-per-stop`}>
                                    <MapPin size={10} className="inline mr-0.5" />
                                    +${vehicle.per_stop.toFixed(2)} per stop
                                  </p>
                                )}
                                {/* Hourly rate - show when hourly trip type or always */}
                                {vehicle.hourly_rate > 0 && (
                                  <p className="text-xs text-blue-600 font-medium mt-0.5" data-testid={`vehicle-${vehicle.id}-hourly`}>
                                    <Clock size={10} className="inline mr-0.5" />
                                    ${vehicle.hourly_rate.toFixed(2)}/hour
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              {vehicle.loading || vehicle.price === null ? (
                                <>
                                  <p className="font-bold text-base sm:text-lg text-gray-400">--</p>
                                  <p className="text-[10px] sm:text-xs text-gray-400">Enter addresses</p>
                                </>
                              ) : (
                                <>
                                  <p className="font-bold text-base sm:text-lg text-amber-600">${vehicle.price.toFixed(2)}</p>
                                  <p className="text-[10px] sm:text-xs text-green-600 font-medium">
                                    {selectedTripType === 'round-trip' ? 'Round Trip' : selectedTripType === 'hourly' ? 'Hourly' : 'One Way'}
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>

                  <Input 
                    placeholder="Special instructions (optional)"
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    className="border-2 focus:border-amber-500"
                  />

                  {/* Promo Code Section */}
                  <Card className="p-4 border-2 border-dashed border-amber-300 bg-amber-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag size={18} className="text-amber-600" />
                      <Label className="font-semibold text-amber-800">Have a promo code?</Label>
                    </div>
                    
                    {appliedPromo ? (
                      <div className="flex items-center justify-between p-3 bg-green-100 rounded-lg">
                        <div>
                          <p className="font-bold text-green-800">{appliedPromo.code}</p>
                          <p className="text-sm text-green-700">
                            You save ${appliedPromo.discount.toFixed(2)}!
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={removePromoCode}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X size={18} />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter code (e.g., SAVE10)"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          className="flex-1 border-2"
                          data-testid="input-promo-code-user"
                        />
                        <Button
                          onClick={validatePromoCode}
                          disabled={isValidatingPromo || !promoCode}
                          className="bg-amber-500 hover:bg-amber-600"
                        >
                          {isValidatingPromo ? <Loader2 size={16} className="animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                    )}
                  </Card>

                  {/* Referral Discount Banner */}
                  {referralDiscount.rides_remaining > 0 && (
                    <Card className="p-3 border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-pink-50">
                      <div className="flex items-center gap-2">
                        <Gift size={20} className="text-purple-600" />
                        <div className="flex-1">
                          <p className="font-semibold text-purple-800">Referral Discount Active!</p>
                          <p className="text-sm text-purple-600">
                            Save ${referralDiscount.discount?.toFixed(2)} on this ride • {referralDiscount.rides_remaining} ride{referralDiscount.rides_remaining > 1 ? 's' : ''} left
                          </p>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Price Summary with All Discounts */}
                  {(appliedPromo || getReferralDiscountAmount() > 0) && vehicleOptions.find(v => v.id === selectedVehicle)?.price && (
                    <Card className="p-4 border-2 bg-gradient-to-r from-green-50 to-emerald-50">
                      <div className="space-y-2">
                        <div className="flex justify-between text-gray-600">
                          <span>Subtotal</span>
                          <span>${vehicleOptions.find(v => v.id === selectedVehicle)?.price.toFixed(2)}</span>
                        </div>
                        {appliedPromo && (
                          <div className="flex justify-between text-green-600">
                            <span>Promo Discount ({appliedPromo.code})</span>
                            <span>-${appliedPromo.discount.toFixed(2)}</span>
                          </div>
                        )}
                        {getReferralDiscountAmount() > 0 && (
                          <div className="flex justify-between text-purple-600">
                            <span>Referral Discount</span>
                            <span>-${getReferralDiscountAmount().toFixed(2)}</span>
                          </div>
                        )}
                        <div className="border-t pt-2 flex justify-between font-bold text-lg">
                          <span>Total</span>
                          <span className="text-green-600">${getFinalPrice().toFixed(2)}</span>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Terms & Conditions Checkbox */}
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border" data-testid="terms-checkbox-section">
                    <button
                      type="button"
                      onClick={() => setTermsAccepted(!termsAccepted)}
                      className="mt-0.5 flex-shrink-0"
                      data-testid="terms-checkbox"
                    >
                      {termsAccepted ? (
                        <CheckSquare className="text-amber-500" size={20} />
                      ) : (
                        <Square className="text-gray-400" size={20} />
                      )}
                    </button>
                    <p className="text-sm text-gray-700">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={openTermsModal}
                        className="text-amber-600 font-semibold underline hover:text-amber-700"
                        data-testid="terms-link"
                      >
                        Terms & Conditions
                      </button>
                    </p>
                  </div>

                  <Button 
                    className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white py-6 text-lg"
                    onClick={handleBooking}
                    disabled={isProcessing || !vehicleOptions.find(v => v.id === selectedVehicle)?.price || (isScheduled && (!scheduledDate || !scheduledTime)) || !termsAccepted}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={20} />
                        Processing...
                      </>
                    ) : !isLoggedIn ? (
                      <>
                        <User className="mr-2" size={20} />
                        Sign In to Book
                      </>
                    ) : isScheduled ? (
                      <>
                        <Calendar className="mr-2" size={20} />
                        Schedule Ride - Pay ${getFinalPrice().toFixed(2)}
                      </>
                    ) : (
                      <>Pay ${vehicleOptions.find(v => v.id === selectedVehicle)?.price ? getFinalPrice().toFixed(2) : '0.00'}</>
                    )}
                  </Button>
                  <p className="text-xs text-center text-gray-500">
                    {!isLoggedIn 
                      ? 'Create an account or sign in to book your ride'
                      : isScheduled && scheduledTime
                        ? `Scheduled for ${new Date(scheduledTime).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} • Payment required now`
                        : 'Payment required before booking confirmation'
                    }
                  </p>
                </TabsContent>

                <TabsContent value="delivery" className="space-y-4">
                  <div className="space-y-4">
                    {/* Contact Info */}
                    <div className="grid grid-cols-2 gap-3">
                      <Input 
                        placeholder="Your name"
                        value={passengerName}
                        onChange={(e) => setPassengerName(e.target.value)}
                        className="border-2 focus:border-amber-500"
                      />
                      <Input 
                        placeholder="Phone number"
                        value={passengerPhone}
                        onChange={(e) => setPassengerPhone(e.target.value)}
                        className="border-2 focus:border-amber-500"
                      />
                    </div>

                    {/* 1. PICKUP */}
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 text-green-600 z-10" size={20} />
                      <AddressAutocomplete
                        placeholder="Pickup address"
                        className="pl-10 py-6 border-2 focus:border-amber-500"
                        value={pickup}
                        onChange={setPickup}
                        onSelect={(loc) => setPickup(loc.address)}
                        data-testid="delivery-input-pickup"
                      />
                    </div>

                    {/* 2. ADD STOPS - Always visible */}
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                        <MapPin size={12} />
                        ADD DELIVERY STOPS (optional, +${getPerStopPrice().toFixed(2)} each) {stops.length > 0 && `- ${stops.length} added`}
                      </p>
                      {/* Existing Stops - scrollable when many */}
                      {stops.length > 0 && (
                        <div className={`space-y-2 ${stops.length > 5 ? 'max-h-48 overflow-y-auto pr-1' : ''}`}>
                          {stops.map((stop, index) => (
                            <div key={index} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-amber-300 shadow-sm">
                              <div className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                                {index + 1}
                              </div>
                              <span className="flex-1 text-sm truncate">{stop.address}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                                onClick={async () => {
                                  isManualPriceUpdate.current = true; // Prevent debounce override
                                  const newStops = stops.filter((_, i) => i !== index);
                                  setStops(newStops);
                                  setNewStopAddress('');
                                  toast({ title: "Stop Removed", description: "Updating price..." });
                                  await updatePricesForStops(newStops.length, 'delivery');
                                }}
                              >
                                <X size={14} />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add New Stop */}
                      {stops.length < 100 && (
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-600 z-10" />
                          <AddressAutocomplete
                            placeholder={`+ Add delivery stop ${stops.length + 1}`}
                            className="pl-10 py-3 border-2 border-dashed border-amber-400 focus:border-amber-500 text-sm bg-white"
                            value={newStopAddress}
                            onChange={setNewStopAddress}
                            onSelect={(loc) => {
                              if (loc.address) {
                                isManualPriceUpdate.current = true; // Prevent debounce override
                                const newStopsCount = stops.length + 1;
                                setStops([...stops, { address: loc.address, lat: loc.lat || 0, lng: loc.lng || 0 }]);
                                setNewStopAddress('');
                                toast({ title: "Stop Added", description: `+$${getPerStopPrice().toFixed(2)} added to fare` });
                                updatePricesForStops(newStopsCount, 'delivery');
                              }
                            }}
                          />
                        </div>
                      )}
                      
                      {stops.length > 0 && (
                        <div className="flex items-center justify-between bg-amber-100 py-1 px-2 rounded">
                          <p className="text-xs text-amber-700 font-semibold">
                            +${(stops.length * getPerStopPrice()).toFixed(2)} for {stops.length} stop{stops.length > 1 ? 's' : ''}
                          </p>
                          {stops.length > 3 && (
                            <button 
                              type="button"
                              onClick={async () => {
                                setStops([]);
                                toast({ title: "All Stops Cleared" });
                                await updatePricesForStops(0, 'delivery');
                              }}
                              className="text-xs text-red-600 hover:text-red-800 underline"
                            >
                              Clear All
                            </button>
                          )}
                        </div>
                      )}
                      
                      {stops.length >= 100 && (
                        <p className="text-xs text-gray-500 italic text-center">Maximum 100 stops reached</p>
                      )}
                    </div>

                    {/* 3. DROPOFF (Final Delivery Destination) */}
                    <div className="relative">
                      <Navigation className="absolute left-3 top-3 text-red-600 z-10" size={20} />
                      <AddressAutocomplete
                        placeholder="Final delivery address"
                        className="pl-10 py-6 border-2 focus:border-amber-500"
                        value={dropoff}
                        onChange={setDropoff}
                        onSelect={(loc) => setDropoff(loc.address)}
                        data-testid="delivery-input-dropoff"
                      />
                    </div>

                    <Input 
                      placeholder="Package description"
                      className="py-6 border-2 focus:border-amber-500"
                      value={packageDescription}
                      onChange={(e) => setPackageDescription(e.target.value)}
                    />
                    <Input 
                      placeholder="Special instructions (optional)"
                      className="py-6 border-2 focus:border-amber-500"
                      value={specialInstructions}
                      onChange={(e) => setSpecialInstructions(e.target.value)}
                    />
                  </div>

                  {/* Trip Type Selection for Delivery */}
                  <div className="space-y-2 pt-2">
                    <h3 className="font-semibold text-sm">Delivery Type</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {tripTypes.filter(t => t.id !== 'hourly').map((type) => (
                        <Card 
                          key={type.id}
                          className={`p-3 cursor-pointer transition-all text-center ${
                            selectedTripType === type.id ? 'border-2 border-amber-500 bg-amber-50' : 'border hover:border-amber-300'
                          }`}
                          onClick={() => setSelectedTripType(type.id)}
                          data-testid={`delivery-trip-type-${type.id}`}
                        >
                          <p className="font-semibold text-sm">{type.name}</p>
                          <p className="text-xs text-gray-500">{type.id === 'round-trip' ? 'Return pickup' : type.description}</p>
                        </Card>
                      ))}
                    </div>
                  </div>

                  {/* Schedule for Later - Delivery - Always Visible */}
                  <div className="space-y-3 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border-2 border-amber-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                          <Calendar className="text-white" size={16} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-sm text-slate-800">Schedule for Later</h3>
                          <p className="text-xs text-gray-500">Book up to 30 days in advance</p>
                        </div>
                      </div>
                      <Switch
                        checked={isScheduled}
                        onCheckedChange={(checked) => {
                          setIsScheduled(checked);
                          if (!checked) {
                            setScheduledDate('');
                            setScheduledTime('');
                          }
                        }}
                        data-testid="delivery-schedule-toggle"
                      />
                    </div>
                    
                    {isScheduled && (
                      <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 space-y-3">
                        <p className="text-xs text-amber-700">
                          Schedule your delivery up to 30 days in advance. We operate 24/7! Red slots are already booked.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-amber-800 text-sm">Date *</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className={`mt-1 w-full justify-start text-left font-normal border-2 border-amber-300 hover:bg-amber-50 ${!scheduledDate && 'text-muted-foreground'}`}
                                  data-testid="delivery-schedule-date"
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4 text-amber-600" />
                                  {scheduledDate ? format(scheduledDate, 'EEE, MMM d') : <span>Select date</span>}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                  mode="single"
                                  selected={scheduledDate}
                                  onSelect={(date) => {
                                    setScheduledDate(date);
                                    setScheduledTime('');
                                    if (date) {
                                      const dateStr = format(date, 'yyyy-MM-dd');
                                      fetchAvailableSlots(dateStr, selectedVehicle);
                                    }
                                  }}
                                  disabled={(date) => date < new Date() || date > addDays(new Date(), 30)}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <Label className="text-amber-800 text-xs sm:text-sm">Time * {isLoadingSlots && <Loader2 className="inline animate-spin ml-1" size={12} />}</Label>
                            {scheduledDate && availableSlots.length > 0 ? (
                              <div className="mt-1 grid grid-cols-4 sm:grid-cols-5 gap-1.5 max-h-48 overflow-y-auto p-2 bg-gradient-to-b from-amber-50/50 to-white rounded-lg border border-amber-200 shadow-inner">
                                {availableSlots.map((slot) => (
                                  <button
                                    key={slot.datetime_utc}
                                    type="button"
                                    disabled={!slot.available}
                                    onClick={() => setScheduledTime(slot.datetime_utc)}
                                    className={`px-1.5 py-2 text-xs font-medium rounded-md transition-all duration-150 ${
                                      scheduledTime === slot.datetime_utc 
                                        ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-md scale-105 ring-2 ring-amber-300' 
                                        : slot.available 
                                          ? 'bg-white text-gray-700 hover:bg-amber-100 hover:text-amber-800 border border-gray-200 hover:border-amber-300 hover:shadow-sm' 
                                          : 'bg-gray-100 text-gray-400 cursor-not-allowed line-through opacity-60'
                                    }`}
                                    title={slot.reason || (slot.available ? 'Available' : 'Booked')}
                                    data-testid={`delivery-time-slot-${slot.time?.replace(/[:\s]/g, '')}`}
                                  >
                                    {slot.time}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="mt-1 p-3 bg-gray-50 rounded border border-amber-300 text-center">
                                <p className="text-xs text-gray-500">{scheduledDate ? 'Loading times...' : 'Select date first'}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {slotError && (
                          <p className="text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle size={12} /> {slotError}
                          </p>
                        )}
                        
                        {scheduledTime && !slotError && (
                          <p className="text-xs text-green-700 flex items-center gap-1">
                            <CheckCircle size={12} /> 
                            Scheduled for {new Date(scheduledTime).toLocaleString([], { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric', 
                              hour: 'numeric', 
                              minute: '2-digit' 
                            })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Return Time for Round Trip Delivery */}
                  {selectedTripType === 'round-trip' && (
                    <div className="space-y-3 pt-2">
                      <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 space-y-3">
                        <div className="flex items-center gap-2">
                          <Clock className="text-blue-500" size={18} />
                          <h3 className="font-semibold text-sm text-blue-800">When do you need the return pickup?</h3>
                        </div>
                        <p className="text-xs text-blue-700">Set the date and time for return delivery pickup</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-blue-700 font-medium">Return Date</label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className={`mt-1 w-full justify-start text-left font-normal border-2 border-blue-300 hover:bg-blue-50 ${!returnDate && 'text-muted-foreground'}`}
                                  data-testid="delivery-return-date"
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4 text-blue-600" />
                                  {returnDate ? format(returnDate, 'EEE, MMM d') : <span>Select date</span>}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                  mode="single"
                                  selected={returnDate}
                                  onSelect={(date) => setReturnDate(date)}
                                  disabled={(date) => date < new Date() || date > addDays(new Date(), 30)}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <label className="text-xs text-blue-700 font-medium">Return Time</label>
                            <Input
                              type="time"
                              value={returnTime}
                              onChange={(e) => setReturnTime(e.target.value)}
                              className="border-2 border-blue-300 focus:border-blue-500 mt-1"
                              data-testid="delivery-return-time"
                            />
                          </div>
                        </div>
                        {returnDate && returnTime && (
                          <p className="text-sm text-blue-800 font-medium">
                            ✓ Return pickup scheduled for {format(returnDate, 'EEE, MMM d')} at {returnTime}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Delivery Vehicle Options */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm">Select Delivery Vehicle</h3>
                    {isFetchingPrices ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin text-amber-500" size={24} />
                        <span className="ml-2 text-gray-500">Loading delivery options...</span>
                      </div>
                    ) : vehicleOptions.length === 0 ? (
                      <Card className="p-4 text-center text-gray-500 border-2">
                        <Package size={32} className="mx-auto mb-2 text-gray-300" />
                        <p>Enter pickup and delivery addresses to see vehicle options</p>
                      </Card>
                    ) : (
                      <div className="grid gap-3">
                        {vehicleOptions.map((vehicle) => (
                          <Card 
                            key={vehicle.id}
                            className={`p-4 cursor-pointer border-2 transition-all ${
                              selectedVehicle === vehicle.id 
                                ? 'border-amber-500 bg-amber-50 shadow-md' 
                                : 'border-gray-200 hover:border-amber-300'
                            }`}
                            onClick={() => setSelectedVehicle(vehicle.id)}
                            data-testid={`delivery-vehicle-${vehicle.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                selectedVehicle === vehicle.id ? 'bg-amber-500' : 'bg-gray-100'
                              }`}>
                                <Package className={selectedVehicle === vehicle.id ? 'text-white' : 'text-gray-500'} size={24} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold">{vehicle.name}</h4>
                                  {selectedVehicle === vehicle.id && (
                                    <CheckCircle size={16} className="text-amber-500" />
                                  )}
                                </div>
                                <p className="text-sm text-gray-500">{vehicle.description || 'Delivery vehicle'}</p>
                                {/* Per-stop price - updates automatically from admin settings */}
                                {vehicle.per_stop && (
                                  <p className="text-xs text-amber-600 font-medium mt-0.5" data-testid={`delivery-vehicle-${vehicle.id}-per-stop`}>
                                    <MapPin size={10} className="inline mr-0.5" />
                                    +${vehicle.per_stop.toFixed(2)} per stop
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                {vehicle.loading || !vehicle.price ? (
                                  <Loader2 size={16} className="animate-spin text-amber-500" />
                                ) : (
                                  <>
                                    <p className="font-bold text-lg text-amber-600">${vehicle.price.toFixed(2)}</p>
                                    <p className="text-xs text-gray-500">{vehicle.time}</p>
                                  </>
                                )}
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Promo Code Section for Delivery */}
                  <Card className="p-4 border-2 border-dashed border-amber-300 bg-amber-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag size={18} className="text-amber-600" />
                      <Label className="font-semibold text-amber-800">Have a promo code?</Label>
                    </div>
                    
                    {appliedPromo ? (
                      <div className="flex items-center justify-between p-3 bg-green-100 rounded-lg">
                        <div>
                          <p className="font-bold text-green-800">{appliedPromo.code}</p>
                          <p className="text-sm text-green-700">
                            You save ${appliedPromo.discount.toFixed(2)}!
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={removePromoCode}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X size={18} />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter code (e.g., SAVE10)"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          className="flex-1 border-2"
                          data-testid="input-promo-code-delivery"
                        />
                        <Button
                          onClick={validatePromoCode}
                          disabled={isValidatingPromo || !promoCode}
                          className="bg-amber-500 hover:bg-amber-600"
                        >
                          {isValidatingPromo ? <Loader2 size={16} className="animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                    )}
                  </Card>

                  {/* Price Summary with Discount for Delivery */}
                  {appliedPromo && vehicleOptions.find(v => v.id === selectedVehicle)?.price && (
                    <Card className="p-4 border-2 bg-gradient-to-r from-green-50 to-emerald-50">
                      <div className="space-y-2">
                        <div className="flex justify-between text-gray-600">
                          <span>Subtotal</span>
                          <span>${vehicleOptions.find(v => v.id === selectedVehicle)?.price.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-green-600">
                          <span>Promo Discount ({appliedPromo.code})</span>
                          <span>-${appliedPromo.discount.toFixed(2)}</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between font-bold text-lg">
                          <span>Total</span>
                          <span className="text-amber-600">${getFinalPrice().toFixed(2)}</span>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Terms & Conditions Checkbox for Delivery */}
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border" data-testid="delivery-terms-checkbox-section">
                    <button
                      type="button"
                      onClick={() => setTermsAccepted(!termsAccepted)}
                      className="mt-0.5 flex-shrink-0"
                      data-testid="delivery-terms-checkbox"
                    >
                      {termsAccepted ? (
                        <CheckSquare className="text-amber-500" size={20} />
                      ) : (
                        <Square className="text-gray-400" size={20} />
                      )}
                    </button>
                    <p className="text-sm text-gray-700">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={openTermsModal}
                        className="text-amber-600 font-semibold underline hover:text-amber-700"
                        data-testid="delivery-terms-link"
                      >
                        Terms & Conditions
                      </button>
                    </p>
                  </div>

                  <Button 
                    className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white py-6 text-lg"
                    onClick={handleBooking}
                    disabled={isProcessing || !vehicleOptions.find(v => v.id === selectedVehicle)?.price || !termsAccepted}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={20} />
                        Processing...
                      </>
                    ) : !isLoggedIn ? (
                      <>
                        <User className="mr-2" size={20} />
                        Sign In to Book
                      </>
                    ) : (
                      <>Pay ${vehicleOptions.find(v => v.id === selectedVehicle)?.price ? getFinalPrice().toFixed(2) : '0.00'}</>
                    )}
                  </Button>
                  <p className="text-xs text-center text-gray-500">
                    {!isLoggedIn 
                      ? 'Create an account or sign in to book your delivery'
                      : 'Payment required before booking confirmation'
                    }
                  </p>
                </TabsContent>
              </Tabs>
            </Card>
          </div>

          {/* Map & Recent Activity */}
          <div className="space-y-6">
            {/* Active Ride Tracker or Map Placeholder */}
            {isLoadingRide ? (
              <Card className="h-80 flex items-center justify-center border-2">
                <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
              </Card>
            ) : activeRide ? (
              <div className="space-y-4">
                {/* Driver Arrival Banner */}
                {driverArrived && (
                  <Card className="p-4 bg-green-50 border-2 border-green-300 animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle size={24} className="text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-green-700">Your Driver Has Arrived!</p>
                        <p className="text-sm text-green-600">Please head to your pickup location.</p>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Driver Location Map */}
                {activeRide.status === 'driver_assigned' && (
                  <DriverLocationMap
                    rideId={activeRide.id}
                    pickup={activeRide.pickup}
                    dropoff={activeRide.dropoff}
                    driverLocation={driverLocation}
                    backendUrl={BACKEND_URL}
                    onOpenChat={() => setShowChat(true)}
                  />
                )}

                {/* Ride Tracker */}
                <RideTracker 
                  ride={activeRide} 
                  driver={rideDriver} 
                  onRefresh={fetchActiveRide}
                />

                {/* Chat Button */}
                {activeRide.driver_id && !showChat && (
                  <Button
                    onClick={() => setShowChat(true)}
                    className="w-full bg-amber-100 hover:bg-amber-200 text-amber-700 border border-amber-300"
                    data-testid="open-chat-btn"
                  >
                    <MessageCircle size={18} className="mr-2" />
                    Message Your Driver
                  </Button>
                )}

                {/* Chat Panel */}
                {showChat && activeRide.driver_id && (
                  <RideChat
                    rideId={activeRide.id}
                    senderId={currentUser?.id}
                    senderType="user"
                    senderName={currentUser?.name || 'Rider'}
                    recipientName={activeRide.driver_name || 'Driver'}
                    backendUrl={BACKEND_URL}
                    onClose={() => setShowChat(false)}
                    newMessage={newChatMessage}
                  />
                )}
              </div>
            ) : (
              <Card className="h-80 bg-slate-800 flex items-center justify-center border-2 border-slate-700">
                <div className="text-center text-slate-400">
                  <MapPin size={48} className="mx-auto mb-2 text-amber-500" />
                  <p className="font-semibold text-white">Map View</p>
                  <p className="text-sm">(Live tracking will appear here when you book a ride)</p>
                </div>
              </Card>
            )}

            {/* Recent Rides */}
            <Card className="p-6 border-2 border-slate-700 bg-white shadow-xl">
              <h3 className="font-semibold text-lg mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {isLoadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin text-amber-500" size={24} />
                    <span className="ml-2 text-gray-500">Loading activity...</span>
                  </div>
                ) : rideHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Car size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No recent activity</p>
                    <p className="text-sm">Your ride history will appear here</p>
                  </div>
                ) : (
                  rideHistory.map((ride) => (
                    <Card key={ride.id} className="p-4 hover:shadow-md transition-shadow border">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {ride.trip_type === 'delivery' ? <Package size={18} className="text-slate-600" /> : <Car size={18} className="text-amber-600" />}
                          <span className="font-semibold">{ride.trip_type === 'delivery' ? 'Delivery' : 'Ride'}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            ride.status === 'completed' ? 'bg-green-100 text-green-700' :
                            ride.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            ride.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {ride.status?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-amber-600">${ride.estimated_price?.toFixed(2) || '0.00'}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1 truncate">{ride.pickup?.address || 'Pickup'}</p>
                      <p className="text-sm text-gray-600 mb-2 truncate">→ {ride.dropoff?.address || 'Dropoff'}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{ride.created_at ? new Date(ride.created_at).toLocaleDateString() : ''}</span>
                        <div className="flex items-center gap-2">
                          {ride.vehicle_type && (
                            <span className="bg-slate-100 px-2 py-0.5 rounded">{ride.vehicle_type}</span>
                          )}
                          {ride.driver_rating && (
                            <div className="flex items-center gap-1">
                              <Star size={12} className="fill-amber-400 text-amber-400" />
                              <span>{ride.driver_rating}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
              {rideHistory.length > 0 && (
                <Button 
                  variant="outline" 
                  className="w-full mt-4 border-2"
                  onClick={fetchRideHistory}
                >
                  <Clock size={16} className="mr-2" />
                  Refresh Activity
                </Button>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Install App Prompt */}
      <InstallPrompt />

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="terms-modal">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden relative animate-in fade-in zoom-in-95">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="text-amber-500" size={24} />
                <h2 className="text-xl font-bold">{termsContent.title}</h2>
              </div>
              <button 
                onClick={() => setShowTermsModal(false)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-terms-modal"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {isLoadingTerms ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-amber-500" size={32} />
                </div>
              ) : termsContent.content ? (
                <div className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
                  {termsContent.content}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  Terms & Conditions have not been set up yet. Please contact support.
                </p>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowTermsModal(false)}
              >
                Close
              </Button>
              <Button
                className="bg-amber-500 hover:bg-amber-600"
                onClick={() => {
                  setTermsAccepted(true);
                  setShowTermsModal(false);
                }}
                data-testid="accept-terms-btn"
              >
                <CheckCircle size={16} className="mr-2" />
                I Accept
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default UserPortal;
